import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Link2, BarChart3, Shield, Zap, Globe, TrendingUp, Smartphone, X, Download, ShieldCheck, UserCheck, AlertTriangle, Clock, Lock, Eye } from 'lucide-react';

const LandingPage = () => {
  const navigate = useNavigate();
  const [showInstallModal, setShowInstallModal] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isIOS, setIsIOS] = useState(false);
  const [isAndroid, setIsAndroid] = useState(false);

  useEffect(() => {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.register('/sw.js').catch(err => console.log('SW error:', err));
    }

    const userAgent = navigator.userAgent.toLowerCase();
    setIsIOS(/iphone|ipad|ipod/.test(userAgent));
    setIsAndroid(/android/.test(userAgent));

    window.addEventListener('beforeinstallprompt', (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    });
  }, []);

  const handleInstallClick = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        setDeferredPrompt(null);
        setShowInstallModal(false);
      }
    } else {
      setShowInstallModal(true);
    }
  };

  // Cam efektli mor çerçeveli kart stili (resim gibi - belirgin mor kenarlık)
  const GlassCard = ({ children, className = "" }) => (
    <div 
      className={`relative rounded-2xl ${className}`}
      style={{
        background: 'linear-gradient(180deg, rgba(20, 10, 40, 0.9) 0%, rgba(10, 5, 25, 0.95) 100%)',
        border: '2px solid rgba(147, 51, 234, 0.6)',
        boxShadow: '0 0 30px rgba(147, 51, 234, 0.3), inset 0 0 60px rgba(147, 51, 234, 0.1)',
        backdropFilter: 'blur(10px)'
      }}
    >
      <div className="relative z-10 p-6">
        {children}
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#0a0a0f] relative overflow-hidden">
      {/* Animated background */}
      <div className="absolute top-20 left-20 w-96 h-96 bg-purple-600/10 rounded-full blur-3xl"></div>
      <div className="absolute bottom-20 right-20 w-96 h-96 bg-violet-600/10 rounded-full blur-3xl"></div>
      <div className="absolute top-1/2 left-1/3 w-64 h-64 bg-purple-500/5 rounded-full blur-2xl"></div>
      
      <div className="relative z-10">
        <nav className="border-b border-white/10 bg-black/30 backdrop-blur-md" data-testid="landing-nav">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
            <motion.div 
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              className="flex items-center gap-2"
            >
              <img src="/sl-logo.png" alt="SnapLink" className="w-10 h-10 rounded-lg" />
              <h1 className="text-lg sm:text-2xl font-bold text-white">SnapLink</h1>
            </motion.div>
            <motion.div 
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              className="flex gap-2 items-center"
            >
              <button
                data-testid="nav-admin-btn"
                onClick={() => navigate('/admin/login')}
                className="px-3 py-2 text-gray-400 hover:text-white transition-colors text-sm"
              >
                Admin
              </button>
              <button
                data-testid="nav-login-btn"
                onClick={() => navigate('/login')}
                className="px-4 py-2 text-sm bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-all"
              >
                Giriş Yap
              </button>
            </motion.div>
          </div>
        </nav>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          {/* Hero Section */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <motion.div
              initial={{ scale: 0.9 }}
              animate={{ scale: 1 }}
              transition={{ duration: 0.5 }}
              className="mb-6"
            >
              <span className="inline-block px-4 py-2 bg-purple-500/10 border border-purple-500/30 rounded-full text-purple-400 text-sm font-semibold mb-6">
                Türkiye'nin En Güvenli Link Kısaltma Servisi
              </span>
            </motion.div>
            <h2 className="text-4xl md:text-6xl font-bold text-white mb-6">
              Linklerinizi
              <span className="text-purple-400"> Güvence</span> Altına Alın
              <br />
              <span className="text-purple-400">Tam Kontrol</span> Sizde
            </h2>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto mb-8 leading-relaxed">
              Gelişmiş anti-bot koruma, gerçek zamanlı tehdit algılama ve admin onaylı güvenlik sistemi ile
              linklerinizi profesyonelce yönetin. Her tıklamayı analiz edin, şüpheli aktiviteleri anında tespit edin.
            </p>

            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="flex gap-4 justify-center flex-wrap mb-8"
            >
              <button
                data-testid="hero-register-btn"
                onClick={() => navigate('/register')}
                className="px-8 py-4 text-lg bg-purple-600 hover:bg-purple-700 text-white rounded-xl transition-all font-semibold"
              >
                Hemen Başla - Ücretsiz
              </button>
              <button
                data-testid="hero-login-btn"
                onClick={() => navigate('/login')}
                className="px-8 py-4 text-lg bg-white/5 hover:bg-white/10 text-white rounded-xl border border-purple-500/30 transition-all"
              >
                Giriş Yap
              </button>
            </motion.div>

            {/* Tanıtım Amaçlı Ücretsiz - Ayrı cam efektli çerçeve */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.4 }}
              className="inline-block"
            >
              <div 
                className="px-8 py-4 rounded-2xl"
                style={{
                  background: 'linear-gradient(180deg, rgba(20, 10, 40, 0.9) 0%, rgba(10, 5, 25, 0.95) 100%)',
                  border: '2px solid rgba(147, 51, 234, 0.6)',
                  boxShadow: '0 0 30px rgba(147, 51, 234, 0.3), inset 0 0 60px rgba(147, 51, 234, 0.1)'
                }}
              >
                <span className="text-purple-300 font-bold text-lg">🎉 Tanıtım Amaçlı Tamamen Ücretsiz</span>
              </div>
            </motion.div>
          </motion.div>

          {/* Güvenlik Özellikleri - Cam efektli kartlar */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.35 }}
            className="mb-16"
          >
            <div className="grid md:grid-cols-2 gap-6">
              {/* Anti-Bot Koruma */}
              <GlassCard>
                <div className="flex items-start gap-4">
                  <div className="w-14 h-14 bg-purple-500/20 rounded-xl flex items-center justify-center flex-shrink-0 border border-purple-500/30">
                    <ShieldCheck className="w-7 h-7 text-purple-400" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-white mb-2">Anti-Bot Koruma Sistemi</h3>
                    <p className="text-gray-400 text-sm leading-relaxed">
                      Yapay zeka destekli bot algılama teknolojimiz, sahte tıklamaları ve otomatik botları 
                      anında tespit eder. Gerçek kullanıcı trafiğinizi koruyun, istatistikleriniz %100 gerçek kalsın.
                    </p>
                  </div>
                </div>
              </GlassCard>

              {/* Admin Onaylı Kayıt */}
              <GlassCard>
                <div className="flex items-start gap-4">
                  <div className="w-14 h-14 bg-violet-500/20 rounded-xl flex items-center justify-center flex-shrink-0 border border-violet-500/30">
                    <UserCheck className="w-7 h-7 text-violet-400" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-white mb-2">Admin Onaylı Kullanıcı Kaydı</h3>
                    <p className="text-gray-400 text-sm leading-relaxed">
                      Her yeni kullanıcı kaydı admin tarafından onaylanır. Spam hesapları ve kötü niyetli 
                      kullanıcıları platformunuzdan uzak tutun, güvenli bir topluluk oluşturun.
                    </p>
                  </div>
                </div>
              </GlassCard>

              {/* Link Onay Sistemi */}
              <GlassCard>
                <div className="flex items-start gap-4">
                  <div className="w-14 h-14 bg-fuchsia-500/20 rounded-xl flex items-center justify-center flex-shrink-0 border border-fuchsia-500/30">
                    <Eye className="w-7 h-7 text-fuchsia-400" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-white mb-2">Link Oluşturma Onay Sistemi</h3>
                    <p className="text-gray-400 text-sm leading-relaxed">
                      Kullanıcılar tarafından oluşturulan her link, yayına alınmadan önce admin onayından geçer.
                      Zararlı, spam veya uygunsuz içerikli linkleri yayına almadan engelleyin.
                    </p>
                  </div>
                </div>
              </GlassCard>

              {/* Kötü Link Tespiti */}
              <GlassCard>
                <div className="flex items-start gap-4">
                  <div className="w-14 h-14 bg-pink-500/20 rounded-xl flex items-center justify-center flex-shrink-0 border border-pink-500/30">
                    <AlertTriangle className="w-7 h-7 text-pink-400" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-white mb-2">Anlık Tehdit Algılama & Askıya Alma</h3>
                    <p className="text-gray-400 text-sm leading-relaxed">
                      Şüpheli aktivite tespit edildiğinde linkler otomatik olarak askıya alınır. 
                      Malware, phishing ve zararlı yönlendirmeleri gerçek zamanlı olarak engelleyin.
                    </p>
                  </div>
                </div>
              </GlassCard>
            </div>
          </motion.div>

          {/* Özellikler Grid - Cam efektli */}
          <motion.div 
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.6 }}
            className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-16"
          >
            {[
              { icon: Zap, title: 'Işık Hızında Yönlendirme', desc: 'Milisaniye cinsinden hızlı yönlendirme, kullanıcılarınız beklemez' },
              { icon: BarChart3, title: 'Gelişmiş Analitik', desc: 'Platform, tarayıcı, şehir ve ülke bazlı detaylı istatistikler' },
              { icon: Shield, title: 'SSL Güvenlik', desc: 'Tüm linkleriniz SSL şifrelemesi ile korunur' },
              { icon: Globe, title: 'Coğrafi Analiz', desc: 'Ziyaretçilerinizin nereden geldiğini harita üzerinde görün' },
              { icon: TrendingUp, title: 'Performans Raporları', desc: 'Günlük, haftalık ve aylık detaylı raporlar' },
              { icon: Clock, title: '14 Gün Veri Saklama', desc: 'Son 14 günün tüm verilerine erişim imkanı' }
            ].map((feature, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 + idx * 0.1 }}
              >
                <GlassCard className="h-full hover:scale-105 transition-transform">
                  <div className="w-12 h-12 bg-purple-500/20 rounded-lg flex items-center justify-center mb-4 border border-purple-500/30">
                    <feature.icon className="w-6 h-6 text-purple-400" />
                  </div>
                  <h3 className="text-xl font-semibold mb-2 text-white">{feature.title}</h3>
                  <p className="text-gray-400 text-sm">{feature.desc}</p>
                </GlassCard>
              </motion.div>
            ))}
          </motion.div>

          {/* Mobil Uygulama - Cam efektli */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            className="mb-16"
          >
            <GlassCard className="text-center">
              <div className="flex items-center justify-center gap-4 mb-4">
                <img src="/sl-logo.png" alt="SnapLink" className="w-16 h-16 rounded-2xl" />
                <div className="text-left">
                  <h3 className="text-2xl font-bold text-white">Mobil Uygulama</h3>
                  <p className="text-gray-400">iPhone ve Android için</p>
                </div>
              </div>
              <p className="text-gray-400 mb-6">Telefonunuzdan linklerinizi yönetin, istatistikleri anlık takip edin!</p>
              <button
                onClick={handleInstallClick}
                className="px-8 py-4 text-lg bg-purple-600 hover:bg-purple-700 text-white rounded-xl transition-all font-semibold flex items-center gap-3 mx-auto"
              >
                <Download className="w-5 h-5" />
                Uygulamayı İndir
              </button>
            </GlassCard>
          </motion.div>

          {/* CTA Section - Cam efektli */}
          <motion.div 
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8 }}
          >
            <GlassCard className="text-center">
              <h3 className="text-3xl md:text-4xl font-bold mb-4 text-white">
                Güvenli Link Yönetimine <span className="text-purple-400">Bugün Başlayın</span>
              </h3>
              <p className="text-gray-400 mb-6 text-lg">
                Tanıtım amaçlı tamamen ücretsiz. Dakikalar içinde başlayın.
              </p>
              <button
                data-testid="cta-btn"
                onClick={() => navigate('/register')}
                className="px-10 py-4 text-lg bg-purple-600 hover:bg-purple-700 text-white rounded-xl transition-all font-semibold"
              >
                Ücretsiz Hesap Oluştur
              </button>
            </GlassCard>
          </motion.div>

          {/* Footer */}
          <motion.footer
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1 }}
            className="mt-20 text-center text-gray-500 text-sm border-t border-white/10 pt-8"
          >
            <div className="flex items-center justify-center gap-2 mb-4">
              <img src="/sl-logo.png" alt="SnapLink" className="w-6 h-6 rounded" />
              <span className="text-white font-semibold">SnapLink</span>
            </div>
            <p>&copy; 2026 SnapLink. Tüm hakları saklıdır.</p>
            <p className="mt-2 text-gray-600">Güvenli Link Kısaltma Servisi</p>
          </motion.footer>
        </div>
      </div>

      {/* PWA Install Modal */}
      <AnimatePresence>
        {showInstallModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4"
            onClick={() => setShowInstallModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              <GlassCard className="max-w-md w-full">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-xl font-bold text-white">Uygulamayı İndir</h3>
                  <button onClick={() => setShowInstallModal(false)} className="text-gray-400 hover:text-white">
                    <X className="w-5 h-5" />
                  </button>
                </div>
                
                <div className="space-y-4">
                  {isIOS && (
                    <div className="bg-purple-500/10 border border-purple-500/30 rounded-xl p-4">
                      <p className="text-white font-semibold mb-2">iPhone / iPad için:</p>
                      <ol className="text-gray-400 text-sm space-y-1 list-decimal list-inside">
                        <li>Safari'de Paylaş butonuna tıklayın</li>
                        <li>"Ana Ekrana Ekle" seçeneğini seçin</li>
                        <li>"Ekle" butonuna basın</li>
                      </ol>
                    </div>
                  )}
                  
                  {isAndroid && (
                    <div className="bg-purple-500/10 border border-purple-500/30 rounded-xl p-4">
                      <p className="text-white font-semibold mb-2">Android için:</p>
                      <ol className="text-gray-400 text-sm space-y-1 list-decimal list-inside">
                        <li>Chrome menüsünü açın (3 nokta)</li>
                        <li>"Ana ekrana ekle" seçin</li>
                        <li>"Ekle" butonuna basın</li>
                      </ol>
                    </div>
                  )}

                  {!isIOS && !isAndroid && (
                    <div className="bg-purple-500/10 border border-purple-500/30 rounded-xl p-4">
                      <p className="text-white font-semibold mb-2">Masaüstü için:</p>
                      <ol className="text-gray-400 text-sm space-y-1 list-decimal list-inside">
                        <li>Adres çubuğundaki yükleme simgesine tıklayın</li>
                        <li>"Yükle" butonuna basın</li>
                      </ol>
                    </div>
                  )}
                </div>
              </GlassCard>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default LandingPage;
